export function formatLocalDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function buildConflictDates(conflicts) {
  const dates = {};
  for (const conflict of conflicts) {
    for (const dateStr of conflict.dates || []) {
      // double_booking takes priority over unblocked
      if (!dates[dateStr] || conflict.type === "double_booking") {
        dates[dateStr] = conflict.type;
      }
    }
  }
  return dates;
}

// Off-platform stays arrive on one channel but must be coloured by their own
// platform (furnished_finder, direct), so they are grouped before being added.
function groupByPlatform(events) {
  const groups = {};
  for (const event of events) {
    const platform = event.platform || "off_platform";
    if (!groups[platform]) groups[platform] = [];
    groups[platform].push(event);
  }
  return groups;
}

export function buildEventMap(airbnb, vrbo, manual, offPlatform = []) {
  const map = {};

  const addEvents = (events, platform) => {
    for (const event of events) {
      const start = new Date(event.start + "T00:00:00");
      const end = new Date(event.end + "T00:00:00");
      const type = event.type || "unknown";
      const title = event.title || "";
      const note = event.note || "";
      const isAvailWindow = event.availability_window || false;
      const windowType = event.window_type || null;
      const windowDuration = event.window_duration || null;
      const startKey = formatLocalDate(start);
      // end is exclusive (checkout day), so last night is end - 1 day
      const lastNight = new Date(end);
      lastNight.setDate(lastNight.getDate() - 1);
      const endKey = formatLocalDate(lastNight);

      for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
        const key = formatLocalDate(d);
        if (!map[key])
          map[key] = {
            platforms: [],
            types: [],
            platformTypes: {},
            platformTitles: {},
            platformNotes: {},
            hasAvailabilityWindow: false,
            hasRealEvent: false,
            windowTypes: {},
            platformAvailWindow: {},
            isStart: false,
            isEnd: false,
          };
        if (!map[key].platforms.includes(platform))
          map[key].platforms.push(platform);
        if (!map[key].types.includes(type)) map[key].types.push(type);
        if (!map[key].platformTypes[platform])
          map[key].platformTypes[platform] = [];
        if (!map[key].platformTypes[platform].includes(type))
          map[key].platformTypes[platform].push(type);
        if (!map[key].platformTitles[platform])
          map[key].platformTitles[platform] = [];
        if (!map[key].platformTitles[platform].includes(title))
          map[key].platformTitles[platform].push(title);
        if (note) {
          if (!map[key].platformNotes[platform])
            map[key].platformNotes[platform] = [];
          if (!map[key].platformNotes[platform].includes(note))
            map[key].platformNotes[platform].push(note);
        }
        if (isAvailWindow) {
          map[key].hasAvailabilityWindow = true;
          map[key].platformAvailWindow[platform] = true;
          if (windowType) {
            map[key].windowTypes[windowType] = windowDuration;
          }
        } else {
          map[key].hasRealEvent = true;
        }
        if (key === startKey) map[key].isStart = true;
        if (key === endKey) map[key].isEnd = true;
      }
    }
  };

  addEvents(airbnb, "airbnb");
  addEvents(vrbo, "vrbo");
  addEvents(manual, "manual");
  for (const [platform, events] of Object.entries(
    groupByPlatform(offPlatform),
  )) {
    addEvents(events, platform);
  }
  return map;
}

// Occupancy for the year the calendar is showing: nights with a confirmed
// booking over nights in the year. A night counts once however many platforms
// show a booking on it (an Airbnb stay mirrored into VRBO is one stay), and
// blocks, tentative holds and availability windows never count, so the figure
// is nights sold, not nights unavailable. With a platform filter only that
// platform's bookings count, matching the filtered grid. `elapsed*` cover the
// nights before `today`, for the year-to-date pace on the current year.
//
// `blockedOnly` is the nights a block or hold covers with no booking behind
// it. Off-platform guests blocked by hand on VRBO/Airbnb but never recorded as
// stays land here, so a large figure means the calendar is fuller than the
// percentage says and the stays want entering.
export function computeOccupancy(
  eventMap,
  year,
  activePlatform = "",
  today = new Date(),
) {
  return computeOccupancyForRange(
    eventMap,
    new Date(year, 0, 1),
    new Date(year + 1, 0, 1),
    activePlatform,
    today,
  );
}

// The twelve months a calendar shows, as {year, month} with month 0-11: a
// calendar year from January, or a rolling window from any month, which
// wraps into the next year.
export function windowMonths(startYear, startMonth) {
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(startYear, startMonth + i, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });
}

// Same figures over any run of nights: `start` inclusive, `end` exclusive.
export function computeOccupancyForRange(
  eventMap,
  start,
  end,
  activePlatform = "",
  today = new Date(),
) {
  const platformsFor = (info) =>
    activePlatform ? [activePlatform] : Object.keys(info.platformTypes);
  const isBooked = (info) =>
    !!info &&
    platformsFor(info).some((platform) =>
      (info.platformTypes[platform] || []).includes("booking"),
    );
  // A real (non-availability-window) hold on one of the platforms in view.
  const isHeld = (info) =>
    !!info &&
    platformsFor(info).some(
      (platform) =>
        info.platformTypes[platform] && !info.platformAvailWindow[platform],
    );

  const todayKey = formatLocalDate(today);
  const totals = {
    nights: 0,
    booked: 0,
    blockedOnly: 0,
    elapsed: 0,
    elapsedBooked: 0,
  };
  for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
    const key = formatLocalDate(d);
    const info = eventMap[key];
    const booked = isBooked(info);
    totals.nights += 1;
    if (booked) totals.booked += 1;
    if (!booked && isHeld(info)) totals.blockedOnly += 1;
    if (key < todayKey) {
      totals.elapsed += 1;
      if (booked) totals.elapsedBooked += 1;
    }
  }

  const percent = (part, whole) =>
    whole > 0 ? Math.round((100 * part) / whole) : null;
  return {
    ...totals,
    percent: percent(totals.booked, totals.nights),
    elapsedPercent: percent(totals.elapsedBooked, totals.elapsed),
  };
}

export function getBookingWindowBoundary(
  airbnb,
  vrbo,
  manualWindows = {},
  activePlatform = "",
) {
  // Booking window shading only applies when filtering to a single platform
  if (!activePlatform) return null;

  // Manual booking window setting takes priority
  const manualConfig = manualWindows[activePlatform];
  if (manualConfig?.boundary_date) {
    return {
      start: manualConfig.boundary_date,
      platforms: [activePlatform],
      source: "booking_window",
      label: `${manualConfig.months} months`,
    };
  }

  // Fall back to auto-detected: use the earliest calendar_horizon event's start
  // (the edge of the platform's iCal feed range)
  const events = activePlatform === "airbnb" ? airbnb : vrbo;
  let minStart = null;

  for (const event of events) {
    if (event.availability_window && event.window_type === "calendar_horizon") {
      if (!minStart || event.start < minStart) {
        minStart = event.start;
      }
    }
  }

  return minStart
    ? {
        start: minStart,
        platforms: [activePlatform],
        source: "calendar_horizon",
      }
    : null;
}

export function classifyDate(
  info,
  conflictType,
  activePlatform,
  dateStr = null,
  bookingWindowBoundary = null,
) {
  const beyondWindow =
    activePlatform &&
    bookingWindowBoundary &&
    dateStr &&
    dateStr >= bookingWindowBoundary.start;

  if (!info && !conflictType) {
    if (beyondWindow) {
      return {
        type: "availability-window",
        platforms: bookingWindowBoundary.platforms,
        platformTitles: {},
      };
    }
    return { type: "empty" };
  }

  if (!info && conflictType) {
    return {
      type: "conflict-only",
      conflictType,
    };
  }

  const isFromActivePlatform =
    !activePlatform || info.platforms.includes(activePlatform);

  const hasAirbnbAndVrbo =
    info.platforms.includes("airbnb") && info.platforms.includes("vrbo");
  const isBookingLike = (types) =>
    types.includes("booking") || types.includes("tentative");
  const hasBothBookings =
    hasAirbnbAndVrbo &&
    isBookingLike(info.platformTypes.airbnb || []) &&
    isBookingLike(info.platformTypes.vrbo || []);
  const isDoubleBooking = hasBothBookings || conflictType === "double_booking";
  const isUnblocked = conflictType === "unblocked";
  const isConflict = isDoubleBooking || isUnblocked;

  const hasActivePlatformAvailWindow =
    activePlatform && info.platformAvailWindow?.[activePlatform];
  const onlyAvailabilityWindow =
    activePlatform && hasActivePlatformAvailWindow && !info.hasRealEvent;

  if (onlyAvailabilityWindow && !isConflict) {
    return {
      type: "availability-window",
      platforms: info.platforms,
      platformTitles: info.platformTitles,
      platformNotes: info.platformNotes,
      windowTypes: info.windowTypes,
    };
  }

  if (isFromActivePlatform || isConflict) {
    return {
      type: "active",
      isDoubleBooking,
      isUnblocked,
      isConflict,
      platforms: info.platforms,
      eventTypes: info.types,
      platformTitles: info.platformTitles,
      platformNotes: info.platformNotes,
      platformTypes: info.platformTypes,
      hasAvailabilityWindow: !!hasActivePlatformAvailWindow,
      windowTypes: info.windowTypes,
      isStart: info.isStart,
      isEnd: info.isEnd,
    };
  }

  // If the only events on this date are availability windows from other platforms,
  // treat as empty (or beyond-window) rather than showing "unavailable"
  if (!info.hasRealEvent) {
    if (beyondWindow) {
      return {
        type: "availability-window",
        platforms: bookingWindowBoundary.platforms,
        platformTitles: {},
      };
    }
    return { type: "empty" };
  }

  return {
    type: "unavailable",
    platforms: info.platforms,
    platformTitles: info.platformTitles,
    platformNotes: info.platformNotes,
  };
}
